// @flow
import {
  canCreateProductFromTemplate,
  canCreateProductByFullUpload,
  canOrderAccessories,
} from '../user-permission/UserPermissionService';
import { isProductTypeAvailable } from '../product-type/ProductTypeService';
import messages from '../../../data/empty-states';

/* Empty state images */
import productDefault from '../../assets/empty-state/product-default.svg';
import emptyStateSearchImage from '../../assets/empty-state/empty-search.svg';
import addTemplatesPhoneImage from '../../assets/empty-state/templates-phone-contact.svg';
import addTemplatesEmailImage from '../../assets/empty-state/templates-email-contact.svg';

import type { ProductSizes } from '../../../api/types/ProductSizes';

export type ContactDetails = {
  firstName: string,
  lastName: string,
  phone: string,
  email: string,
  type: string,
};

export type Contacts = {
  toPopulateProductPage: {
    email: string,
    firstName: string,
    lastName: string,
    phone: string,
    type: string,
  },
  hasFilters: boolean,
};

type GetDesktopEmptyStateMessage = {
  contacts: Contacts,
  productTypeId: string,
  productName: string,
  hasFilters: boolean,
  productSizesAvailable: ProductSizes,
  permissions: Array<string>,
  isApprovalsView: boolean,
  isAccessoriesView: boolean,
};

type MobileEmptyStateImage = {
  productName: string,
  hasFilters: boolean,
  isApprovalsView: boolean,
  isAccessoriesView: boolean,
  contacts: Contacts,
  permissions: Array<string>,
};

const filteredEmptySearchMessage = () => ({
  image: emptyStateSearchImage,
  title: messages.filteredEmptySearchTitle,
  text: messages.filteredEmptySearchText,
});

const mobileBrowserMessage = (productName: string) => ({
  image: addTemplatesEmailImage,
  title: messages.mobileEmptySearchTitle,
  text: messages.mobileEmptySearchText,
  params: {
    text: { productName },
  },
});

const noProductsCreatedByUser = (productName: string) => ({
  image: productDefault,
  title: messages.noProductsCreateTitle,
  text: messages.noProductsCreateText,
  params: {
    text: { productName },
  },
});

const noProductsCreatedByUserFullUpload = (productName: string) => ({
  image: productDefault,
  title: messages.noProductsCreateTitleFullUpload,
  text: messages.noProductsCreateTextFullUpload,
  params: {
    text: { productName },
  },
});

const contactAccountManagerToAddTemplates = (productName: string, contactDetails: ContactDetails) => ({
  contactDetails,
  image: addTemplatesPhoneImage,
  title: messages.noTemplatesTitle,
  text: messages.noTemplatesContactAccountManagerText,
  params: {
    title: { productName },
  },
});

const contactCustomerServiceToAddTemplates = (productName: string, contactDetails: ContactDetails) => ({
  contactDetails,
  image: addTemplatesPhoneImage,
  title: messages.noTemplatesTitle,
  text: messages.noTemplatesContactCustomerServiceText,
  params: {
    title: { productName },
  },
});

const contactAdminToAddTemplates = (productName: string, contactDetails: ContactDetails) => ({
  contactDetails,
  image: addTemplatesEmailImage,
  title: messages.noTemplatesTitle,
  text: messages.noTemplatesContactAdministratorText,
  params: {
    title: { productName },
  },
});

const contactToAddTemplatesFullUpload = () => ({
  fullUploadText: messages.noTemplatesFullUploadText,
});

const noProductsSharedWithUser = (contactDetails: ContactDetails) => ({
  contactDetails,
  image: productDefault,
  title: messages.noProductsContactTitle,
  text: messages.noProductsContactText,
});

const noProductsNoAccessToTemplatesFullUpload = () => ({
  image: productDefault,
  title: messages.noProductsCreateTitleFullUpload,
  text: messages.noProductsNoTemplateFullUploadText,
});

const cannotOrderAccessories = (contactDetails: ContactDetails) => ({
  contactDetails,
  image: addTemplatesEmailImage,
  title: messages.cannotOrderAccessoriesTitle,
  text: messages.cannotOrderAccessoriesText,
});

const getMobileEmptyStateImage = ({
  productName,
  hasFilters,
  isAccessoriesView,
  contacts,
  permissions,
}: MobileEmptyStateImage) => {
  if (hasFilters) {
    return filteredEmptySearchMessage();
  }

  if (isAccessoriesView && !canOrderAccessories(permissions)) {
    return cannotOrderAccessories(contacts.toPopulateProductPage);
  }

  // No templates and/or products
  return mobileBrowserMessage(productName);
};

const contactGetterByType = {
  accountManager: contactAccountManagerToAddTemplates,
  customerService: contactCustomerServiceToAddTemplates,
  primaryContact: contactAdminToAddTemplates,
};

const getContactInfo = (productName: string, contactData: ContactDetails) => {
  return contactGetterByType[contactData.type](productName, contactData);
};

// eslint-disable-next-line complexity
const getDesktopEmptyStateMessage = ({
  contacts,
  productTypeId,
  productName,
  hasFilters,
  productSizesAvailable,
  permissions,
  isAccessoriesView,
}: GetDesktopEmptyStateMessage) => {
  if (hasFilters) {
    return filteredEmptySearchMessage();
  }

  if (isAccessoriesView && !canOrderAccessories(permissions)) {
    return cannotOrderAccessories(contacts.toPopulateProductPage);
  }

  const contactData = contacts.toPopulateProductPage;
  const hasTemplates = isProductTypeAvailable(productTypeId, productSizesAvailable);
  const canCreateProducts = canCreateProductFromTemplate(permissions);
  const canFullUploadProducts = canCreateProductByFullUpload(permissions);

  /* Checks for access to product creation */
  if (canFullUploadProducts && !canCreateProducts) {
    return noProductsNoAccessToTemplatesFullUpload();
  }

  if (!canCreateProducts) {
    return noProductsSharedWithUser(contactData);
  }

  /* Checks for templates availability (has access to creation but no available templates) */
  if (hasTemplates && !canFullUploadProducts) {
    return noProductsCreatedByUser(productName);
  }

  if (hasTemplates && canFullUploadProducts) {
    return noProductsCreatedByUserFullUpload(productName);
  }

  if (!hasTemplates && canFullUploadProducts) {
    const templateMessage = getContactInfo(productName, contactData);
    const fullUploadMessage = contactToAddTemplatesFullUpload();
    const result = { ...templateMessage, ...fullUploadMessage }; // merge objects to add the messaging for full upload

    return result;
  }

  return getContactInfo(productName, contactData);
};

export { getMobileEmptyStateImage, getDesktopEmptyStateMessage };
